import React, { useEffect, useState } from 'react';
import api from '../api';
import { RefreshCw, ShieldAlert, Mail, AlertOctagon, CheckCircle2, Filter, Inbox } from 'lucide-react';

const ACTION_COLORS = {
  'reject':         { color: '#ef4444', label: 'Rejeitado',     icon: 'err' },
  'soft reject':    { color: '#f97316', label: 'Soft reject',   icon: 'warn' },
  'rewrite subject':{ color: '#a855f7', label: 'Subject ⚠',    icon: 'warn' },
  'add header':     { color: '#3b82f6', label: 'Header ⚠',     icon: 'warn' },
  'greylist':       { color: '#06b6d4', label: 'Greylist',      icon: 'warn' },
  'no action':      { color: '#10b981', label: 'Aceito',        icon: 'ok' },
};

export default function Rspamd() {
  const [stat, setStat] = useState(null);
  const [history, setHistory] = useState([]);
  const [errors, setErrors] = useState([]);
  const [actions, setActions] = useState(null);
  const [graph, setGraph] = useState(null);
  const [graphType, setGraphType] = useState('hourly');
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [s, h, e, a, g] = await Promise.allSettled([
        api.get('/rspamd/stat'),
        api.get('/rspamd/history'),
        api.get('/rspamd/errors'),
        api.get('/rspamd/actions'),
        api.get(`/rspamd/graph?type=${graphType}`),
      ]);
      if (s.status === 'fulfilled') setStat(s.value.data);
      if (h.status === 'fulfilled') setHistory(Array.isArray(h.value.data?.rows) ? h.value.data.rows : (h.value.data || []));
      if (e.status === 'fulfilled') setErrors(e.value.data || []);
      if (a.status === 'fulfilled') setActions(a.value.data);
      if (g.status === 'fulfilled') setGraph(g.value.data);
      if (s.status === 'rejected') setErr(s.reason?.response?.data?.error || 'Rspamd inacessível');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [graphType]);

  if (err) return (
    <>
      <div className="page-title"><h1>Rspamd · Anti-Spam</h1></div>
      <div className="err-msg">{err}</div>
      <button onClick={load} className="ghost"><RefreshCw size={14}/> Tentar de novo</button>
    </>
  );

  // Métricas calculadas
  const total = stat?.scanned || 0;
  const spamCount = stat?.spam_count || 0;
  const hamCount = stat?.ham_count || 0;
  const actionsCount = stat?.actions || {};
  const rejected = actionsCount['reject'] || 0;
  const passed = actionsCount['no action'] || 0;
  const uptime = stat?.uptime ? formatUptime(stat.uptime) : '–';
  const avgScanTime = stat?.scan_times?.filter(t => t)?.reduce((a,b,_,arr) => a + b/arr.length, 0) || 0;

  return (
    <>
      <div className="page-title">
        <h1><ShieldAlert size={20} style={{verticalAlign:'middle'}}/> Rspamd · Anti-Spam</h1>
        <div>
          <span style={{color:'#a1a1aa', fontSize:'.8rem', marginRight:'.6rem'}}>auto-refresh 15s</span>
          <button onClick={load} className="ghost" disabled={loading}>
            <RefreshCw size={14}/> {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        </div>
      </div>

      {stat && (
        <>
          <div className="cards">
            <div className="card">
              <div className="label">Total escaneado</div>
              <div className="value">{total.toLocaleString('pt-BR')}</div>
            </div>
            <div className="card">
              <div className="label">Aceitos (no action)</div>
              <div className="value ok">{passed.toLocaleString('pt-BR')}</div>
            </div>
            <div className="card">
              <div className="label">Rejeitados (spam)</div>
              <div className="value err">{rejected.toLocaleString('pt-BR')}</div>
            </div>
            <div className="card">
              <div className="label">Tempo médio de scan</div>
              <div className="value">{(avgScanTime * 1000).toFixed(0)} ms</div>
            </div>
            <div className="card">
              <div className="label">Uptime</div>
              <div className="value" style={{fontSize:'1.1rem'}}>{uptime}</div>
            </div>
            <div className="card">
              <div className="label">Versão Rspamd</div>
              <div className="value" style={{fontSize:'1.1rem'}}>{stat.version}</div>
            </div>
          </div>
        </>
      )}

      <div className="tab-bar">
        <div className={'tab ' + (tab==='overview'?'active':'')} onClick={()=>setTab('overview')}><Inbox size={14} style={{verticalAlign:'middle'}}/> Distribuição</div>
        <div className={'tab ' + (tab==='history'?'active':'')} onClick={()=>setTab('history')}><Mail size={14} style={{verticalAlign:'middle'}}/> Histórico</div>
        <div className={'tab ' + (tab==='errors'?'active':'')} onClick={()=>setTab('errors')}><AlertOctagon size={14} style={{verticalAlign:'middle'}}/> Erros ({errors.length})</div>
        <div className={'tab ' + (tab==='actions'?'active':'')} onClick={()=>setTab('actions')}><Filter size={14} style={{verticalAlign:'middle'}}/> Thresholds</div>
      </div>

      {tab === 'overview' && (
        <>
          <h3>Distribuição por ação</h3>
          {Object.keys(actionsCount).length === 0 ? <p style={{color:'#a1a1aa'}}>Sem dados ainda.</p> : (
            <table>
              <thead><tr><th>Ação</th><th>Mensagens</th><th>% do total</th><th style={{width:'40%'}}>Distribuição</th></tr></thead>
              <tbody>
                {Object.entries(actionsCount).map(([name, count]) => {
                  const pct = total ? (count / total) * 100 : 0;
                  const meta = ACTION_COLORS[name] || { color: '#a1a1aa', label: name };
                  return (
                    <tr key={name}>
                      <td><span className={'badge ' + (meta.icon || 'ok')}>{meta.label}</span></td>
                      <td>{count.toLocaleString('pt-BR')}</td>
                      <td>{pct.toFixed(1)}%</td>
                      <td>
                        <div style={{background:'#27272a', borderRadius:'4px', overflow:'hidden', height:'12px'}}>
                          <div style={{background: meta.color, width: pct + '%', height:'100%'}}/>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {actions && (
            <>
              <h3 style={{marginTop:'1.5rem'}}>Estatísticas Bayes</h3>
              <div className="row" style={{gap:'1rem'}}>
                <div className="card" style={{flex:1}}>
                  <div className="label">Mensagens aprendidas</div>
                  <div className="value">{(stat?.total_learns || 0).toLocaleString('pt-BR')}</div>
                </div>
                <div className="card" style={{flex:1}}>
                  <div className="label">Conexões ativas</div>
                  <div className="value">{stat?.connections || 0}</div>
                </div>
                <div className="card" style={{flex:1}}>
                  <div className="label">Pools alocados</div>
                  <div className="value">{stat?.pools_allocated || 0}</div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'history' && (
        <>
          <h3>Últimas mensagens escaneadas</h3>
          {history.length === 0 ? <p style={{color:'#a1a1aa'}}>Nenhuma mensagem ainda. Quando o servidor receber emails, aparecem aqui.</p> : (
            <table>
              <thead>
                <tr>
                  <th>Quando</th><th>De</th><th>Para</th><th>Score</th><th>Ação</th><th>Símbolos</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 50).map((m, i) => {
                  const meta = ACTION_COLORS[m.action] || { color: '#a1a1aa', label: m.action, icon: 'ok' };
                  const scoreColor = m.score < 5 ? '#10b981' : m.score < 10 ? '#f59e0b' : '#ef4444';
                  return (
                    <tr key={i}>
                      <td style={{color:'#a1a1aa', fontSize:'.78rem', whiteSpace:'nowrap'}}>{new Date(m.unix_time * 1000).toLocaleString('pt-BR')}</td>
                      <td style={{fontFamily:'JetBrains Mono', fontSize:'.78rem'}}>{m.from_mime || m.from_smtp || '-'}</td>
                      <td style={{fontFamily:'JetBrains Mono', fontSize:'.78rem'}}>{(m.rcpt_mime || m.rcpt_smtp || []).join(', ').slice(0, 40)}</td>
                      <td style={{color: scoreColor, fontWeight:'600'}}>{m.score?.toFixed(2)}</td>
                      <td><span className={'badge ' + (meta.icon || 'ok')}>{meta.label}</span></td>
                      <td style={{fontFamily:'JetBrains Mono', fontSize:'.7rem', maxWidth:'350px'}}>
                        {(m.symbols || []).slice(0,5).map(s => s.name).join(', ')}
                        {(m.symbols?.length || 0) > 5 && ` +${m.symbols.length - 5}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'errors' && (
        <>
          <h3>Erros recentes</h3>
          {errors.length === 0 ? <p style={{color:'#a1a1aa'}}>Sem erros — Rspamd operando normalmente.</p> : (
            <table>
              <thead><tr><th>Quando</th><th>Tipo</th><th>Mensagem</th></tr></thead>
              <tbody>
                {errors.slice(0, 50).map((e, i) => (
                  <tr key={i}>
                    <td style={{color:'#a1a1aa', fontSize:'.78rem'}}>{new Date((e.ts || 0) * 1000).toLocaleString('pt-BR')}</td>
                    <td><span className="badge err">{e.type}</span></td>
                    <td style={{fontSize:'.82rem', fontFamily:'JetBrains Mono'}}>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {tab === 'actions' && (
        <>
          <h3>Thresholds (limites de pontuação)</h3>
          <p style={{color:'#a1a1aa', fontSize:'.85rem'}}>
            Cada email recebe uma pontuação de "spamicidade". Quando atinge um threshold, a ação correspondente é aplicada.
          </p>
          {actions ? (
            <table>
              <thead><tr><th>Ação</th><th>Threshold (score)</th></tr></thead>
              <tbody>
                {Object.entries(actions).map(([name, score]) => {
                  const meta = ACTION_COLORS[name] || { label: name, icon: 'ok' };
                  return (
                    <tr key={name}>
                      <td><span className={'badge ' + (meta.icon || 'ok')}>{meta.label}</span></td>
                      <td style={{fontFamily:'JetBrains Mono'}}>{typeof score === 'number' ? score.toFixed(2) : score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <p style={{color:'#a1a1aa'}}>Carregando…</p>}
        </>
      )}

      <div style={{marginTop:'2rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.82rem', color:'#a1a1aa'}}>
        <strong style={{color:'#3b82f6'}}>ℹ️ Dashboard nativo do Rspamd</strong>
        <p style={{marginTop:'.3rem'}}>
          A UI completa do Rspamd (com gráficos, treinamento Bayes interativo, scan manual) fica em
          <code> http://localhost:11334</code> dentro do servidor. Acesse via SSH tunnel:
        </p>
        <pre className="code" style={{marginTop:'.4rem'}}>ssh -L 11334:127.0.0.1:11334 root@203.0.113.10</pre>
        <p>Senha do Rspamd está em Credenciais.</p>
      </div>
    </>
  );
}

function formatUptime(s) {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
